import { useEffect, useState, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import Lenis from "lenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CursorNotes from "@/components/CursorNotes";
import ComingSoon from "@/pages/ComingSoon";
import LegalPage from "@/components/LegalPage";
import { TERMS, PRIVACY } from "@/data/legalContent";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import Discover from "@/pages/Discover";
import Access from "@/pages/Access";
import Playlists from "@/pages/Playlists";
import Sessions from "@/pages/Sessions";
import Partners from "@/pages/Partners";
import Pricing from "@/pages/Pricing";
import Faq from "@/pages/Faq";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen grid place-items-center bg-ink text-chalk px-6 text-center">
          <div>
            <p className="eyebrow justify-center">Tap In</p>
            <h1 className="font-display text-2xl mt-4">Something skipped a beat.</h1>
            <button className="btn-primary mt-8" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem("tapin-unlocked") === "1";
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  return (
    <div className="app-root">
      <div className="grain-overlay" aria-hidden="true" />
      {unlocked ? (
        <>
          <CursorNotes />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/discover" element={<Discover />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/access" element={<Access />} />
                <Route path="/privacy" element={<LegalPage doc={PRIVACY} />} />
                <Route path="/terms" element={<LegalPage doc={TERMS} />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </>
      ) : (
        <ComingSoon onUnlock={() => setUnlocked(true)} />
      )}
    </div>
  );
}

export default App;
